/**
 * Feature flags admin client. Consumes the P0 feature-flag surface
 * at /v1/foundational/feature-flags. Phase A - Discovery-owned flag
 * plane. The `source` discriminator from migration 0035 surfaces in
 * a Phase B follow-up; today the API returns `scope ∈ {tenant, global,
 * default}` which is sufficient for the UI's needs.
 */

import { apiFetch } from './api';

export type FlagScope = 'tenant' | 'global' | 'default';

export interface Flag {
  key: string;
  scope: FlagScope;
  enabled: boolean;
  variant: string;
  payload: Record<string, unknown>;
  description: string | null;
  rolloutPercentage: number;
  updatedAt: string | null;
}

export interface ListFlagsResponse {
  flags: Flag[];
}

export interface UpsertFlagBody {
  enabled: boolean;
  variant: string;
  payload?: Record<string, unknown>;
  description?: string;
  rolloutPercentage?: number;
}

export async function listFlags(): Promise<Flag[]> {
  const r = await apiFetch<ListFlagsResponse>('/v1/foundational/feature-flags');
  return r.flags;
}

export async function upsertTenantFlag(
  key: string,
  body: UpsertFlagBody,
): Promise<Flag> {
  return apiFetch<Flag>(`/v1/foundational/feature-flags/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteTenantFlag(key: string): Promise<void> {
  await apiFetch<void>(`/v1/foundational/feature-flags/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

export async function upsertGlobalFlag(
  key: string,
  body: UpsertFlagBody,
): Promise<Flag> {
  return apiFetch<Flag>(`/v1/foundational/feature-flags/${encodeURIComponent(key)}/global`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export const SCOPE_META: Record<FlagScope, { label: string; tone: 'success' | 'warning' | 'glass'; description: string }> = {
  tenant: {
    label: 'Tenant override',
    tone: 'success',
    description: 'A tenant-specific row exists in feature_flags. Overrides the global default.',
  },
  global: {
    label: 'Global',
    tone: 'warning',
    description: 'No tenant row; the global default row applies to all tenants.',
  },
  default: {
    label: 'Catalog default',
    tone: 'glass',
    description: 'Neither tenant nor global row exists; the value comes from the catalog default.',
  },
};
